import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { post, put } from "../../common/api/apiShared";
import { Result } from "../../common/api/result";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { useTranslation } from "../../localization/useTranslation";
import { IAddEditRoleDialogProps } from "./AddEditRoleDialog";
import { RolesMasterDataSaveModel } from "./AddEditRoleDialog.types";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { RoleViewModel } from "../Roles.types";

export function useAddEditRoleDialogHooks(props: IAddEditRoleDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const [errors, setErrors] = useState({ userRolesNameError: "" });
    const [saveModel, setSaveModel] = useState<RolesMasterDataSaveModel | undefined>(getSaveModel());

    const saveMutation = useDotLegalMutation(save);
    const createUserRole = async () => {
        await saveMutation.mutateAsync(undefined, {
            onSuccess: (response: Result<RoleViewModel>) => {
                snackbar.show(createElement(Trans, { i18nKey: "userRolesCreated", values: { name: saveModel?.name } }));
                props.onSave(response.value());
            },
        });
    };

    const editMutation = useDotLegalMutation(update);
    const updateUserRole = () => {
        editMutation.mutateAsync(undefined, {
            onSuccess: () => {
                snackbar.show(createElement(Trans, { i18nKey: "userRolesUpdated", values: { name: saveModel!.name } }));
                props.onSave();
            },
        });
    };

    function validateRolesMasterDataName(name: string) {
        let tempErrors = { ...errors };

        tempErrors.userRolesNameError = isNullOrWhitespace(name) ? translateString("fieldRequired") : "";

        setErrors(tempErrors);
    }

    function getSaveModel() {
        return {
            name: props.selectedRoleViewModel?.name ?? "",
            description: props.selectedRoleViewModel?.description ?? "",
            canOnlyAccessDataWhereResponsible: props.selectedRoleViewModel?.canOnlyAccessDataWhereResponsible ?? false,
            canBeChosenAsResponsible: props.selectedRoleViewModel?.canBeChosenAsResponsible ?? true,
        };
    }

    function validateRolesMasterDataSaveModel(name: string) {
        let formValid = true;
        let tempErrors = { ...errors };

        if (isNullOrWhitespace(name)) {
            tempErrors.userRolesNameError = translateString("fieldRequired");
            formValid = false;
        }

        if (formValid) {
            if (isEdit()) updateUserRole();
            else createUserRole();
        } else {
            setErrors(tempErrors);
        }
    }

    function update() {
        return put(`/roles/${props.selectedRoleViewModel?.id}`, saveModel);
    }

    function save() {
        return post<RoleViewModel>("/roles", saveModel);
    }

    function isEdit() {
        return props.selectedRoleViewModel !== undefined;
    }

    return {
        inProgress: editMutation.isLoading || saveMutation.isLoading,
        saveModel,
        setSaveModel,
        errors,
        validateRolesMasterDataName,
        validateRolesMasterDataSaveModel,
        isEdit,
    };
}
